import axios, { AxiosProgressEvent, AxiosResponse } from "axios";
import i18next, { t } from "i18next";
import qs from "query-string";
import RouteService from "src/services/routeService";
import { invalidateOnSuccess, transformErrorResponse } from "src/store/helpers";
import { ApiResponse, BasicApiResponse, PaginatedResponse } from "src/types";

import { BaseQueryFn, EndpointBuilder } from "@reduxjs/toolkit/query";
import AuthService from "@services/authService";
import { showErrorToast, showWarningToast } from "@shared/toasts/Toasts";

import { FileInfo } from "../types";

import apiClient, { GetPresignedUrlPayload } from "./apiClient";

const pageIndexOffset = 1;

type FileBuilder = EndpointBuilder<BaseQueryFn, "Files", "filesApi">;

export type GetFilesParams = {
    filename?: string;
    workspaceId?: number;
    workspaceGuid?: string;
    workspaces?: number[];
    groups?: number[];
    uploaders?: string[];
    devices?: number[];
    sourceTypes?: number[];
    createdFrom?: string;
    createdTo?: string;
    modifiedFrom?: string;
    modifiedTo?: string;
    uploadStatus?: 0 | 1;
    contentTypes?: string[];
    pageIndex?: number;
    pageSize?: number;
    sortCriteria?: string;
    sortDirection?: string;
    visibilities?: number[];
    includeParentFiles?: boolean;
};

export const defaultPageSize = 200;

type GetFilesResponse = PaginatedResponse<API.File>;

function getFiles(builder: FileBuilder) {
    return builder.query<GetFilesResponse, GetFilesParams>({
        providesTags: ["Files"],
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("error.files.failedToFetchFiles")),
        transformResponse: (response: ApiResponse<GetFilesResponse>) => response.model,
        query: async (params) => {
            const base = await RouteService.getStorageRoute();
            const pageIndex = params?.pageIndex ?? 0;
            const queryParams = {
                ...params,
                pageSize: params?.pageSize ?? defaultPageSize,
                pageIndex: pageIndex + pageIndexOffset,
                arrayFormat: "index",
            };
            return {
                url: base + "files",
                params: queryParams,
                paramsSerializer: () => qs.stringify(queryParams, { arrayFormat: "index" }),
            };
        },
    });
}

type DeleteFilePayload = string;
type DeleteFileResponse = AxiosResponse<BasicApiResponse>;

function deleteFile(builder: FileBuilder) {
    return builder.mutation<DeleteFileResponse, DeleteFilePayload>({
        query: async (fileId) => {
            const base = await RouteService.getStorageRoute();
            return {
                url: base + "files",
                method: "DELETE",
                data: [fileId],
            };
        },
        invalidatesTags: invalidateOnSuccess("Files"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.files.failedToDeleteFile")),
    });
}

type UpdateFilePayload = Partial<Omit<FileInfo, "metadata">> & { id: string };
type UpdateFileResponse = AxiosResponse<BasicApiResponse>;

function updateFile(builder: FileBuilder) {
    return builder.mutation<UpdateFileResponse, UpdateFilePayload>({
        query: async (payload) => {
            const base = await RouteService.getStorageRoute();
            return {
                url: `${base}files/${payload.id}`,
                method: "PATCH",
                data: payload,
            };
        },
        invalidatesTags: invalidateOnSuccess("Files"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.files.failedToUpdateFile")),
    });
}

type UploadFilePayload = GetPresignedUrlPayload & {
    fileToUpload: File;
    controller: AbortController;
    onUploadProgress: (progressEvent: AxiosProgressEvent) => void;
    setUploadedFileIds: React.Dispatch<React.SetStateAction<string[]>>;
};

function uploadFile(builder: FileBuilder) {
    return builder.mutation<FileInfo, UploadFilePayload>({
        queryFn: async (payload) => {
            try {
                const response = await apiClient.getPresignedUrl(payload);

                payload.setUploadedFileIds((prev) => [...prev, response.data.model.fileId]);
                const presignedUrl = response.data.model.uploadPresignedUrl;

                const presignedApi = axios.create();
                await presignedApi.put<File, AxiosResponse<unknown>>(presignedUrl, payload.fileToUpload, {
                    headers: { "Content-Type": payload.fileToUpload.type },
                    onUploadProgress: payload.onUploadProgress,
                    signal: payload.controller.signal,
                });
                const userId = AuthService.getCurrentUser()?.sub;
                if (!userId) {
                    return { error: { message: i18next.t("common:toast.unexpectedError") } };
                }

                return {
                    data: {
                        id: response.data.model.fileId,
                        contentType: payload.fileToUpload.type,
                        createdDate: new Date().toISOString(),
                        modifiedDate: new Date().toISOString(),
                        filename: payload.fileToUpload.name,
                        groupId: payload.groupId,
                        workspaceId: payload.workspaceId,
                        ownerId: userId,
                        size: payload.fileToUpload.size,
                        rolloutPercentage: 0,
                        status: 0,
                        source: 0,
                    },
                };
            } catch (error) {
                const uploadError = error as Error;
                if (uploadError.message.includes("canceled")) {
                    showWarningToast(t("common:section.uploadProvider.error.uploadCanceled"));
                } else {
                    showErrorToast(uploadError.message);
                }

                return { error };
            }
        },
        invalidatesTags: invalidateOnSuccess("Files"),
    });
}

type UpdateFileMetadataPayload = {
    fileId: string;
    metadata: { tags: API.Tag[] };
};
type UpdateFileMetadataResponse = AxiosResponse<BasicApiResponse>;

function updateFileMetadata(builder: FileBuilder) {
    return builder.mutation<UpdateFileMetadataResponse, UpdateFileMetadataPayload>({
        query: async ({ fileId, metadata }) => {
            const base = await RouteService.getStorageRoute();
            return {
                url: `${base}files/${fileId}`,
                method: "PATCH",
                data: { metadata },
            };
        },
        invalidatesTags: invalidateOnSuccess("Files"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.files.failedToUpdateFileMetadata")),
    });
}

type UpdateFileRolloutPayload = { id: string; rolloutPercentage: number };
type UpdateFileRolloutResponse = AxiosResponse<BasicApiResponse>;

function updateFileRollout(builder: FileBuilder) {
    return builder.mutation<UpdateFileRolloutResponse, UpdateFileRolloutPayload>({
        query: async ({ id, rolloutPercentage }) => {
            const base = await RouteService.getStorageRoute();
            return {
                url: `${base}files/${id}/rollout`,
                method: "PATCH",
                headers: { Accept: "text/plain" },
                params: { rolloutPercentage },
                data: null,
            };
        },
        invalidatesTags: invalidateOnSuccess("Files"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.files.failedToUpdateFileRollout")),
    });
}

export default {
    deleteFile,
    uploadFile,
    updateFile,
    updateFileMetadata,
    updateFileRollout,
    getFiles,
};
